// dependencies
import React from "react";
import PropTypes from "prop-types";
import { View, StyleSheet } from "react-native";
import { useIntl } from "react-intl";

// components
import Button from "./Button";
import Text from "../Text";
import BaseComponent from "../BaseComponent";

// libraries
import { emToPx } from "../../libraries";
import { get } from "@cloudspire/legacy-shared/src/libraries";

// theme
import theming from "../../constants/theming";

const { useTheme } = theming;

const styles = StyleSheet.create({
  view: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    backgroundColor: "#ffffff",
  },
  itemCountView: {
    marginLeft: emToPx(1),
  },
  itemCountText: {
    fontSize: emToPx(0.875),
  },
  navigationView: {
    flexDirection: "row",
  },
  pageCountView: {
    flexDirection: "row",
    alignItems: "center",
    marginRight: emToPx(1),
  },
  pageCountText: {
    fontSize: emToPx(0.875),
  },
  navigationArrowView: {
    flexDirection: "row",
  },
});

const Pagination = (props) => {
  const {
    itemCount,
    currentPage,
    itemPerPage,
    backwardButtonProps,
    previousButtonProps,
    nextButtonProps,
    forwardButtonProps,
    shouldRenderBackwardButton = true,
    shouldRenderForwardButton = true,
    shouldRenderItemCount = true,
    shouldRenderPageCount = true,
  } = props;

  const theme = useTheme();
  const intl = useIntl();

  if (itemCount <= itemPerPage) {
    // Si la pagination est inutile, on ne l'affiche pas.
    return null;
  }

  if (itemPerPage <= 0) {
    console.warn(
      "<Pagination> prop `itemPerPage` cannot be inferior or equal to `0`"
    );

    return null;
  }

  const maxPage = Math.ceil(itemCount / itemPerPage);

  return (
    <BaseComponent childrenStyle={styles.view}>
      <View style={styles.itemCountView}>
        {shouldRenderItemCount && (
          <Text style={styles.itemCountText} accessibilityRole="label">
            {intl.formatMessage(
              {
                defaultMessage:
                  "{itemCount, plural, =0 {résultat} one {# résultat} other {# résultats}}",
              },
              { itemCount }
            )}
          </Text>
        )}
      </View>
      <View style={styles.navigationView}>
        <View style={styles.pageCountView}>
          {shouldRenderPageCount && (
            <Text style={styles.pageCountText} accessibilityRole="label">
              {intl.formatMessage(
                {
                  defaultMessage: "Page {pageCount} / {maxPage}",
                },
                { pageCount: Math.min(currentPage, maxPage), maxPage }
              )}
            </Text>
          )}
        </View>

        <View style={styles.navigationArrowView}>
          {shouldRenderBackwardButton && (
            <Button
              {...backwardButtonProps}
              disabled={
                1 === currentPage || get(backwardButtonProps, "disabled", false)
              }
              type="backward"
              theme={theme}
            />
          )}

          <Button
            {...previousButtonProps}
            disabled={
              1 === currentPage || get(previousButtonProps, "disabled", false)
            }
            type="previous"
            theme={theme}
          />

          <Button
            {...nextButtonProps}
            disabled={
              currentPage === Math.ceil(itemCount / itemPerPage) ||
              get(nextButtonProps, "disabled", false)
            }
            type="next"
            theme={theme}
          />

          {shouldRenderForwardButton && (
            <Button
              {...forwardButtonProps}
              disabled={
                currentPage === Math.ceil(itemCount / itemPerPage) ||
                get(forwardButtonProps, "disabled", false)
              }
              type="forward"
              theme={theme}
            />
          )}
        </View>
      </View>
    </BaseComponent>
  );
};

Pagination.propTypes = {
  itemCount: PropTypes.any,
  currentPage: PropTypes.any,
  itemPerPage: PropTypes.any,
  backwardButtonProps: PropTypes.object,
  previousButtonProps: PropTypes.object,
  nextButtonProps: PropTypes.object,
  forwardButtonProps: PropTypes.object,
  shouldRenderBackwardButton: PropTypes.bool,
  shouldRenderForwardButton: PropTypes.bool,
  shouldRenderItemCount: PropTypes.bool,
  shouldRenderPageCount: PropTypes.bool,
};

export default Pagination;
